import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import WheyIcon from '@assets/icons/whey.svg';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AssignSmartOrderParams } from '@/hooks/use-order-inbounds-detail';
import { captureException } from '@/utils/sentry';
import {
  CorrectionPreviewMap,
  OrderAgentWorkflowMode,
  OrderInboundAssignedEmployee,
  OrderMetadata,
  SwapSuggestionMap,
  UnknownContactInfo,
  VariantPackOrderCountsMap,
} from './types';
import { createStyles } from '@/contexts/theme-context';
import {
  DETAIL_CONTENT_LEGIBLE_MIN_WIDTH,
  InboundDetailSplitContainer,
} from './inbound-detail-split-container';
import { EmployeeRestaurant, useUserContext } from '@/contexts/user-context';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import {
  FILTER_CLOSED_STATUSES,
  InboundListFilter,
} from './order-inbounds-order-list/types';
import {
  GET_VARIANT_PACK_SUBSTITUTIONS,
  ORDER_INBOUNDS_DETAIL_QUERY,
  useAssignSmartOrder,
  useSmartOrderSearchTermCorrections,
  useUpdateInboundListQueryCache,
} from './queries';
import {
  getOrderMetadata,
  getRejectedBatchKey,
  getWorkflowModeForStatuses,
  PROCESSING_STATUSES,
} from './utils';
import { HeaderBackButton } from '@/components/header-back-button';
import { InboundListExpandButton } from './inbound-list-expand-button';
import { OrderInboundsDetail } from './order-inbounds-detail';
import { OriginalInboundSkeleton } from './original-inbound-skeleton';
import { partnerSupplier } from '@/utils/environment';
import {
  RegenerateSmartOrderParams,
  useRegenerateSmartOrder,
} from '@/hooks/use-regenerate-smart-order';
import { SCREEN_PADDING } from '@/styles';
import { ScrollViewProps, View } from 'react-native';
import { SmartOrderProcessingStatus, SmartOrderStatus } from '@/enums';
import { Type } from '@/components/toast';
import { useAdaptVariantPackToItemListOption } from '@/contexts/item-context-utils';
import { useBaseSupplierItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOrderInboundsStore } from '@/stores/order-inbounds-store';
import { useOverrideActiveRestaurant } from '@/contexts/user-override-active-restaurant';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useThreadOrderEventsContext } from './thread-order-events-context';
import { useToasts } from '@/contexts/toast-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';
import type {
  ItemsContext_VariantsPackFieldsFragment,
  OrderInbound_GetVariantPackSubstitutionsQuery,
  OrderInbound_GetVariantPackSubstitutionsQueryVariables,
  OrderInbounds_DetailQuery,
  OrderInbounds_DetailQueryVariables,
  SmartOrderFieldsFragment,
  VariantPackOrderCount,
} from '@/types';

export interface Props {
  children?: React.ReactNode;
  initialMergeUUID?: string;
  initialProcessing: boolean;
  initialRejectedUpdatedAt?: string;
  initialRestaurantUUID: string | null;
  initialSubmittedOrderUUID?: string;
  initialThreadIds?: string[];
  isInboundsListExpanded: boolean;
  onAssignedEmployeeChanged?: (
    assignedEmployee: OrderInboundAssignedEmployee | null,
  ) => void;
  onAssignSmartOrder?: (orderUUID: string, restaurantUUID: string) => void;
  onClose?: () => void;
  onOrderIdentityChanged?: (identity: {
    restaurantName: string | null;
    supplierUUID: string;
  }) => void;
  onRegenerateSmartOrder?: (orderUUID: string, restaurantUUID: string) => void;
  orderUUID: string;
  scrollViewProps?: ScrollViewProps;
  selectNextSmartOrder: (smartOrderUUID: string) => void;
  setIsInboundsListExpanded: (isInboundsListExpanded: boolean) => void;
  setOrderInboundsListDrawerOpen?: (isOpen: boolean) => void;
  unknownContactInfo?: UnknownContactInfo;
}

interface OrderStatusInfo {
  createdAt: string;
  status: SmartOrderStatus;
  threadId?: string | null;
}

const isSubmittedStatus = (status: SmartOrderStatus) =>
  status === SmartOrderStatus.SUBMITTED ||
  status === SmartOrderStatus.MEMORIZED;

const isStatusSubmitted = (orders: OrderStatusInfo[]) => {
  // In a thread view, every order in the thread must be submitted
  if (orders.some((o) => o.threadId)) {
    return orders.every((o) => isSubmittedStatus(o.status));
  }
  return orders.some((o) => isSubmittedStatus(o.status));
};

const isStatusRejected = (orders: OrderStatusInfo[]) => {
  if (
    orders
      .filter((o) => !o.threadId)
      .some((o) => o.status === SmartOrderStatus.REJECTED)
  ) {
    return true;
  }
  const newestPerThread = new Map<string, OrderStatusInfo>();
  for (const order of orders) {
    if (!order.threadId) {
      continue;
    }
    const existing = newestPerThread.get(order.threadId);
    if (!existing || order.createdAt > existing.createdAt) {
      newestPerThread.set(order.threadId, order);
    }
  }
  return Array.from(newestPerThread.values()).some(
    (o) => o.status === SmartOrderStatus.REJECTED,
  );
};

const isMemorizedStatus = (status: SmartOrderStatus) =>
  status === SmartOrderStatus.MEMORIZED;

const isStatusMemorized = (orders: OrderStatusInfo[]) => {
  if (orders.some((o) => o.threadId)) {
    return orders.every((o) => isMemorizedStatus(o.status));
  }
  return orders.some((o) => isMemorizedStatus(o.status));
};

const isExactlySubmittedStatus = (status: SmartOrderStatus) =>
  status === SmartOrderStatus.SUBMITTED;

// Unlike isStatusSubmitted (which also matches MEMORIZED), this is used to
// gate whether a submitted order can be opened for viewing, so MEMORIZED
// orders (which stay unviewable) must not match here.
const isStatusExactlySubmitted = (orders: OrderStatusInfo[]) => {
  if (orders.some((o) => o.threadId)) {
    return orders.every((o) => isExactlySubmittedStatus(o.status));
  }
  return orders.some((o) => isExactlySubmittedStatus(o.status));
};

// MEMORIZED orders are never viewable. A SUBMITTED or REJECTED order is
// viewable (read-only) if it was already closed when the screen was opened,
// but still blocked if it transitions to submitted/rejected while being
// actively viewed.
const shouldBlockOrders = (
  orders: OrderStatusInfo[],
  wasAlreadyClosed: boolean,
) =>
  isStatusMemorized(orders) ||
  ((isStatusExactlySubmitted(orders) || isStatusRejected(orders)) &&
    !wasAlreadyClosed);

const getOrderStatusInfos = (
  smartOrderDetails: NonNullable<
    OrderInbounds_DetailQuery['getSmartOrderDetails']
  >,
  isEmailThreadsEnabled: boolean,
): OrderStatusInfo[] =>
  [...smartOrderDetails.map((item) => item.smart_order)].reduce<
    OrderStatusInfo[]
  >((acc, order) => {
    if (order?.status) {
      acc.push({
        createdAt: order.created_at,
        status: order.status as SmartOrderStatus,
        threadId: isEmailThreadsEnabled ? order.thread_id : undefined,
      });
    }
    return acc;
  }, []);

const getInboundStatusMessage = (orders: OrderStatusInfo[]) => {
  if (isStatusSubmitted(orders)) {
    return i18n.t('order_inbound_error.submitted');
  }

  if (isStatusRejected(orders)) {
    return i18n.t('order_inbound_error.rejected');
  }

  return null;
};

interface SuggestionMatch {
  variant_pack?: ItemsContext_VariantsPackFieldsFragment | null;
  variant_pack_order_counts?: VariantPackOrderCount[] | null;
}

const getSuggestions = (
  matches: SuggestionMatch[],
  orderCountsMap: VariantPackOrderCountsMap,
) => {
  const matchesSet = new Set();
  const suggestions: ItemsContext_VariantsPackFieldsFragment[] = [];
  for (const match of matches) {
    if (match.variant_pack && match.variant_pack_order_counts) {
      if (!matchesSet.has(match.variant_pack.uuid)) {
        matchesSet.add(match.variant_pack.uuid);
        suggestions.push(match.variant_pack);
      }
      orderCountsMap.set(
        match.variant_pack.uuid,
        match.variant_pack_order_counts ?? [],
      );
    }
  }
  return suggestions;
};

const getCorrectionKey = (smartOrderUUID: string, searchTermUUID: string) =>
  `${smartOrderUUID}:${searchTermUUID}`;

interface ReassignOrderHelperParams {
  employeeRestaurant: EmployeeRestaurant;
  employeeUUID?: string;
  orderUUIDs: Set<string>;
  reassignFunction: () => Promise<void>;
  skipLoadingState?: boolean;
}

export const OrderInboundWrapper: React.FC<Props> = ({
  children,
  initialMergeUUID,
  initialProcessing,
  initialRejectedUpdatedAt,
  initialSubmittedOrderUUID,
  initialThreadIds,
  isInboundsListExpanded,
  initialRestaurantUUID,
  onAssignedEmployeeChanged,
  onOrderIdentityChanged,
  onAssignSmartOrder,
  onRegenerateSmartOrder,
  orderUUID,
  selectNextSmartOrder,
  setIsInboundsListExpanded,
  unknownContactInfo,
  setOrderInboundsListDrawerOpen,
  ...restDetailProps
}) => {
  const itemQueryVariables = useBaseSupplierItemQueryVariables();
  const [styles] = useStyles();
  const { showErrorToast, showToast } = useToasts();
  const isEmailThreadsEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'email_threads_enabled',
  );
  const hasShownReassignmentToast = useRef(false);
  const { isSmallLayout, isSmallTabletLayout, isLargeLayout } =
    useLayoutContext();
  const { isLoading: isNewOrderLoading } = useNewOrderContext();

  const { selectedRestaurantUUID, restaurantUUIDToRelationshipMap } =
    useUserContext();
  const statusReportedRef = useRef(false);
  const { emitThreadOrderProcessed, onThreadOrderProcessing } =
    useThreadOrderEventsContext();
  const isProcessingReplyRef = useRef(false);
  const [isProcessingReply, setIsProcessingReply] = useState(false);
  const [replyHydrationKey, setReplyHydrationKey] = useState(0);

  // use a ref for fallback value to not trigger query variable changes
  const fallbackRestaurantUUID = useRef(selectedRestaurantUUID);

  // Track if component is still mounted to prevent activeRestaurant updates after unmount
  // TODO: restructure this in some way to fix this.
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Use polling to pick up changes from the server
  // manually control processing status to ensure all smart order data is updated
  // before showing the form
  const [isProcessing, setIsProcessing] = useState(initialProcessing);
  const previousProcessingState = useRef(initialProcessing);
  const [hasPollingError, setHasPollingError] = useState(false);

  // For user controlled processing changes, avoid querying and acting on
  // in-flight poll results while reprocessing
  const refetchInFlight = useRef(false);
  const [isRegeneratingSmartOrders, setIsRegeneratingSmartOrders] =
    useState(false);
  const removedOrderUUIDs = useOrderInboundsStore(
    (store) => store.removedOrderUUIDs,
  );
  const activeStatusFilter = useOrderInboundsStore(
    (store) => store.activeStatusFilter,
  );

  const [mergeUUID, setMergeUUID] = useState<string | undefined>(
    initialMergeUUID,
  );
  const mergeUUIDRef = useRef<string | undefined>(initialMergeUUID);

  const setNextMergeUUID = useCallback((nextMergeUUID: string | undefined) => {
    if (mergeUUIDRef.current !== nextMergeUUID) {
      mergeUUIDRef.current = nextMergeUUID;
      setMergeUUID(nextMergeUUID);
    }
  }, []);

  const { regenerateSmartOrders } = useRegenerateSmartOrder();
  const [assignSmartOrderMutation] = useAssignSmartOrder();
  const updateInboundListQueryCache = useUpdateInboundListQueryCache();

  const detailQueryVariables =
    useMemo<OrderInbounds_DetailQueryVariables>(() => {
      return {
        ...itemQueryVariables,
        businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
        mergeUUID,
        // when there's no assigned restaurant, there will be no VP matches
        // so the restaurantUUID variable won't be used, so it's safe to fall back
        restaurantUUID: initialRestaurantUUID ?? fallbackRestaurantUUID.current,
        skipGroupMemberships: true,
        smartOrderUUID: orderUUID,
        threadIds: initialThreadIds,
      };
    }, [
      itemQueryVariables,
      mergeUUID,
      initialThreadIds,
      orderUUID,
      initialRestaurantUUID,
    ]);

  const {
    data,
    loading: detailQueryLoading,
    error,
    refetch,
    client,
    hasResponse: detailQueryHasResponse,
  } = useWrappedQuery<
    OrderInbounds_DetailQuery,
    OrderInbounds_DetailQueryVariables
  >(ORDER_INBOUNDS_DETAIL_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'network-only',
    variables: detailQueryVariables,
  });

  useEffect(() => {
    if (!initialThreadIds?.length) {
      return;
    }
    return onThreadOrderProcessing(({ threadId }) => {
      if (initialThreadIds.includes(threadId)) {
        isProcessingReplyRef.current = true;
        setIsProcessingReply(true);
        previousProcessingState.current = true;
        refetch();
      }
    });
  }, [onThreadOrderProcessing, initialThreadIds, refetch]);

  // When navigating fresh to a thread where a reply was already processing on mount,
  // the onThreadOrderProcessing event was emitted before this component mounted and
  // isProcessingReplyRef was never set. Detect it from the initial data load so
  // handleProcessingComplete knows to re-hydrate the form when processing finishes.
  const hasInitializedReplyRef = useRef(false);
  useEffect(() => {
    if (hasInitializedReplyRef.current || !data?.getSmartOrderDetails) {
      return;
    }

    hasInitializedReplyRef.current = true;

    let hasProcessingOrder = false;
    let smartOrderCount = 0;

    for (const d of data.getSmartOrderDetails) {
      if (!d.smart_order) {
        continue;
      }

      smartOrderCount++;

      if (
        PROCESSING_STATUSES.has(
          d.smart_order.processing_status as SmartOrderProcessingStatus,
        )
      ) {
        hasProcessingOrder = true;
      }
    }

    if (hasProcessingOrder && smartOrderCount > 1) {
      isProcessingReplyRef.current = true;
      setIsProcessingReply(true);
    }
  }, [data?.getSmartOrderDetails]);

  const activeRestaurant = useMemo(() => {
    if (!data?.getSmartOrderDetails) {
      return null;
    }

    const smartOrderDetails = data.getSmartOrderDetails;
    const assignedRestaurant = smartOrderDetails.find(
      (order) => order.smart_order?.restaurant?.uuid,
    )?.smart_order?.restaurant;
    if (
      assignedRestaurant &&
      restaurantUUIDToRelationshipMap[assignedRestaurant.uuid]
    ) {
      return restaurantUUIDToRelationshipMap[assignedRestaurant.uuid];
    }
    return null;
  }, [data?.getSmartOrderDetails, restaurantUUIDToRelationshipMap]);

  useOverrideActiveRestaurant(activeRestaurant);

  const variantPackIds = useMemo(() => {
    return Boolean(data?.getSmartOrderDetails)
      ? data!.getSmartOrderDetails
          .map((order) =>
            order.search_inputs.map((input) => input.variant_pack_id ?? ''),
          )
          .flat()
          .filter((variantPackId) => variantPackId !== '')
      : [];
  }, [data]);

  const skipVPSubsRequest =
    variantPackIds.length === 0 ||
    !Boolean(data?.getSmartOrderDetails) ||
    !Boolean(activeRestaurant);

  const { data: vpSubData, hasResponse: vpSubDataHasResponse } =
    useWrappedQuery<
      OrderInbound_GetVariantPackSubstitutionsQuery,
      OrderInbound_GetVariantPackSubstitutionsQueryVariables
    >(GET_VARIANT_PACK_SUBSTITUTIONS, {
      context: {
        noBatch: true,
      },
      fetchPolicy: 'network-only',
      skip: skipVPSubsRequest,
      variables: {
        ...itemQueryVariables,
        restaurantUUID: activeRestaurant?.uuid ?? '',
        skipGroupMemberships: true,
        supplierId: activeRestaurant?.supplierUUID ?? '',
        variantPackIds,
      },
    });

  const waitingForSubsData = !skipVPSubsRequest && !vpSubDataHasResponse;
  const loading = detailQueryLoading || waitingForSubsData;

  // Track if we've successfully loaded initial data to distinguish between
  // initial load (show loading skeleton) and refetch (keep form mounted)
  const hasInitialData = useRef(false);
  useEffect(() => {
    if (detailQueryHasResponse && !loading) {
      hasInitialData.current = true;
    }
  }, [data?.getSmartOrderDetails, loading, detailQueryHasResponse]);

  // The query above fetches the whole thread/merge group regardless of status.
  // Narrow it down for the view:
  // - In the Closed list, only closed orders belong in the detail. An email
  //   thread can hold open replies alongside a closed one, and letting them
  //   through would flip the detail into the submission flow.
  // - For a thread whose replies were closed separately -- submitted as
  //   different real orders, or rejected in different batches -- only consider
  //   the subset belonging to the selected list row.
  const visibleSmartOrderDetails = useMemo(() => {
    let details = data?.getSmartOrderDetails;
    if (!details) {
      return details;
    }
    if (activeStatusFilter === InboundListFilter.Closed) {
      details = details.filter(
        (item) =>
          item.smart_order &&
          FILTER_CLOSED_STATUSES.has(
            item.smart_order.status as SmartOrderStatus,
          ),
      );
    }
    if (initialSubmittedOrderUUID) {
      details = details.filter(
        (item) => item.smart_order?.order_uuid === initialSubmittedOrderUUID,
      );
    } else if (initialRejectedUpdatedAt) {
      const batchKey = getRejectedBatchKey(initialRejectedUpdatedAt);
      details = details.filter(
        (item) =>
          item.smart_order?.status === SmartOrderStatus.REJECTED &&
          getRejectedBatchKey(item.smart_order.updated_at) === batchKey,
      );
    }
    return details;
  }, [
    data?.getSmartOrderDetails,
    activeStatusFilter,
    initialRejectedUpdatedAt,
    initialSubmittedOrderUUID,
  ]);

  // Track whether the order was already SUBMITTED or REJECTED (closed) the
  // first time we saw any status for it, so viewing an already-closed order
  // can be told apart from a live transition into submitted/rejected while
  // it's being actively viewed. Captured synchronously during render (not
  // inside an effect) so it's already settled in this same pass for
  // errorText below, which is a useMemo and wouldn't otherwise re-run just
  // because a ref changed. Snapshotted over visibleSmartOrderDetails (not
  // the full thread/merge group) so this agrees with the set
  // shouldBlockOrders is later evaluated against below and in the
  // navigate-away effect.
  const wasAlreadyClosedRef = useRef<boolean | null>(null);
  if (wasAlreadyClosedRef.current === null && visibleSmartOrderDetails) {
    const statusInfos = getOrderStatusInfos(
      visibleSmartOrderDetails,
      isEmailThreadsEnabled,
    );
    wasAlreadyClosedRef.current =
      isStatusExactlySubmitted(statusInfos) || isStatusRejected(statusInfos);
  }
  const wasAlreadyClosed = wasAlreadyClosedRef.current ?? false;

  // Only show loading skeleton for initial load, not during refetches
  const isInitialLoad = !hasInitialData.current && loading;

  // TODO: remove after moving to subscription
  // Right now processing status is controlled by the polling query
  // to ensure we block rendering until we refetch after processing.
  // With async regeneration, there's a delay between the regenerate mutation
  // and the polling query that results in the form rendering.
  // Take order's actual processing status into account to prevent that
  const isOrderProcessing = useMemo(() => {
    const ordersAreProcessing = (data?.getSmartOrderDetails || []).some(
      (orderDetails) => {
        const order = orderDetails.smart_order;
        return (
          order &&
          PROCESSING_STATUSES.has(
            order.processing_status as SmartOrderProcessingStatus,
          )
        );
      },
    );
    return isProcessing || isRegeneratingSmartOrders || ordersAreProcessing;
  }, [isProcessing, isRegeneratingSmartOrders, data?.getSmartOrderDetails]);

  const updateCachedProcessingStatus = useCallback(
    (orderUUIDs: Set<string>, processingStatus: SmartOrderProcessingStatus) => {
      updateInboundListQueryCache(client, (cachedData) => {
        return {
          ...cachedData,
          smart_order: cachedData.smart_order.map((order) => {
            if (orderUUIDs.has(order.uuid)) {
              return {
                ...order,
                processing_status: processingStatus,
              };
            }
            return order;
          }),
        };
      });
    },
    [client, updateInboundListQueryCache],
  );

  const reassignOrderHelper = useCallback(
    async ({
      employeeRestaurant,
      orderUUIDs,
      reassignFunction,
      skipLoadingState = false,
    }: ReassignOrderHelperParams) => {
      if (!skipLoadingState) {
        setIsRegeneratingSmartOrders(true);
      }
      refetchInFlight.current = true;
      try {
        if (!skipLoadingState) {
          updateCachedProcessingStatus(
            orderUUIDs,
            SmartOrderProcessingStatus.PROCESSING,
          );
        }
        await reassignFunction();

        if (!isMountedRef.current) {
          return;
        }

        // if the regeneration is done asynchronously, don't refetch and let the
        // poll handle the refetch, restaurant, and processing status updates
        if (skipLoadingState) {
          // we have to manually refetch because we need the correct search_inputs
          // to hydrate the form once we reset the isRegeneratingSmartOrders state
          // TODO: see if we can fetch all SmartOrderFields off of the assign
          // and reassign mutations
          await refetch({
            ...detailQueryVariables,
            restaurantUUID: employeeRestaurant.uuid,
          });

          // if the user abandons the screen before the refetch is complete,
          // don't continue or risk updating the active restaurant when looking
          // at a different order
          if (!isMountedRef.current) {
            return;
          }

          if (!hasShownReassignmentToast.current) {
            hasShownReassignmentToast.current = true;
            showToast({
              content: i18n.t(
                'order_inbound_processing.complete_for_restaurant',
                {
                  restaurantName: employeeRestaurant.name,
                },
              ),
              type: Type.POSITIVE,
            });
          }
        }
      } catch {
        if (isMountedRef.current) {
          showErrorToast();
          updateCachedProcessingStatus(
            orderUUIDs,
            SmartOrderProcessingStatus.ERROR,
          );
        }
      } finally {
        if (isMountedRef.current) {
          setIsRegeneratingSmartOrders(false);
        }
        refetchInFlight.current = false;
      }
    },
    [
      detailQueryVariables,
      refetch,
      showErrorToast,
      showToast,
      updateCachedProcessingStatus,
    ],
  );

  const assignUnknownContact = useCallback(
    async ({
      employeeUUID,
      employeeRestaurant,
      skipLoadingState,
    }: AssignSmartOrderParams) => {
      const orderUUIDs = new Set([orderUUID]);
      const reassignFunction = async () => {
        await assignSmartOrderMutation({
          variables: {
            employeeUUID,
            restaurantUUID: employeeRestaurant.uuid,
            smartOrderUUID: orderUUID,
            supplierUUID: employeeRestaurant.supplierUUID,
          },
        });
        onAssignSmartOrder?.(orderUUID, employeeRestaurant.uuid);
      };
      await reassignOrderHelper({
        employeeRestaurant,
        orderUUIDs,
        reassignFunction,
        skipLoadingState,
      });
    },
    [
      assignSmartOrderMutation,
      orderUUID,
      reassignOrderHelper,
      onAssignSmartOrder,
    ],
  );

  const reassignSmartOrders = useCallback(
    async (regenerateParams: RegenerateSmartOrderParams) => {
      const orderUUIDs = new Set(regenerateParams.smartOrderUUIDs);
      const reassignFunction = async () => {
        await regenerateSmartOrders(regenerateParams);
        onRegenerateSmartOrder?.(
          regenerateParams.smartOrderUUIDs[0],
          regenerateParams.employeeRestaurant.uuid,
        );
      };
      await reassignOrderHelper({
        employeeRestaurant: regenerateParams.employeeRestaurant,
        orderUUIDs,
        reassignFunction,
      });
    },
    [regenerateSmartOrders, reassignOrderHelper, onRegenerateSmartOrder],
  );

  const onCloseProp = restDetailProps.onClose;

  // Navigate away if the order is submitted or rejected
  useEffect(() => {
    if (!visibleSmartOrderDetails) {
      return;
    }

    // Check for status changes that should close the detail
    const orders = getOrderStatusInfos(
      visibleSmartOrderDetails,
      isEmailThreadsEnabled,
    );

    const shouldClose = shouldBlockOrders(orders, wasAlreadyClosed);

    const message = getInboundStatusMessage(orders);

    if (shouldClose && message) {
      const wasRemovedLocally =
        removedOrderUUIDs.has(orderUUID) &&
        (isStatusSubmitted(orders) || isStatusRejected(orders));

      // Skip toast for any orders that have been submitted or removed by this user
      if (!wasRemovedLocally) {
        showToast({
          content: message,
        });
      }
      onCloseProp?.();
      return;
    }
  }, [
    visibleSmartOrderDetails,
    removedOrderUUIDs,
    orderUUID,
    showToast,
    onCloseProp,
    isEmailThreadsEnabled,
    wasAlreadyClosed,
  ]);

  // Handle status and processing status changes on live updates
  useEffect(() => {
    if (!data?.getSmartOrderDetails) {
      return;
    }

    const smartOrderDetails = data.getSmartOrderDetails;

    const assignedRestaurantUUID = smartOrderDetails.find(
      (order) => order.smart_order?.restaurant?.uuid,
    )?.smart_order?.restaurant?.uuid;
    const assignedRestaurant = assignedRestaurantUUID
      ? restaurantUUIDToRelationshipMap[assignedRestaurantUUID]
      : null;

    // Handle processing status changes
    const currentProcessingState = smartOrderDetails.some(
      (order) =>
        order.smart_order?.processing_status &&
        PROCESSING_STATUSES.has(
          order.smart_order.processing_status as SmartOrderProcessingStatus,
        ),
    );

    // Only act on actual state transitions
    if (previousProcessingState.current && !currentProcessingState) {
      previousProcessingState.current = currentProcessingState;

      const handleProcessingComplete = async () => {
        try {
          // refetch with the correct restaurantUUID in case it was unassigned
          const updatedVariables = {
            ...detailQueryVariables,
            restaurantUUID: assignedRestaurant?.uuid ?? selectedRestaurantUUID,
          };
          await refetch(updatedVariables);
          const isReply = isProcessingReplyRef.current;
          isProcessingReplyRef.current = false;
          setIsProcessingReply(false);
          setReplyHydrationKey((k) => k + 1);
          initialThreadIds?.forEach((threadId) => {
            emitThreadOrderProcessed({
              isReply,
              smartOrderUUID: orderUUID,
              threadId,
            });
          });
          setIsProcessing(false);
          if (!hasShownReassignmentToast.current) {
            hasShownReassignmentToast.current = true;
            showToast({
              content: assignedRestaurant?.name
                ? i18n.t('order_inbound_processing.complete_for_restaurant', {
                    restaurantName: assignedRestaurant.name,
                  })
                : i18n.t('order_inbound_processing.complete'),
              type: Type.POSITIVE,
            });
          }
        } catch {
          setHasPollingError(true);
        }
      };

      handleProcessingComplete();
    } else if (!previousProcessingState.current && currentProcessingState) {
      previousProcessingState.current = currentProcessingState;
      setIsProcessing(true);
      setReplyHydrationKey((k) => k + 1);
    }
  }, [
    data?.getSmartOrderDetails,
    activeRestaurant?.uuid,
    restaurantUUIDToRelationshipMap,
    detailQueryVariables,
    selectedRestaurantUUID,
    refetch,
    showToast,
    emitThreadOrderProcessed,
    initialThreadIds,
    orderUUID,
    isEmailThreadsEnabled,
  ]);

  // handle mergeUUID changes to ensure query is refetched
  useEffect(() => {
    if (!data?.getSmartOrderDetails) {
      return;
    }

    const mergeUUIDs = data.getSmartOrderDetails.reduce<Set<string>>(
      (acc, item) => {
        if (item.smart_order?.merge_uuid) {
          acc.add(item.smart_order.merge_uuid);
        }
        return acc;
      },
      new Set(),
    );

    // Guard against edge case where there are multiple merge UUIDs
    // by only fetching the group if there is exactly one merge UUID
    const nextMergeUUID = Array.from(mergeUUIDs)[0];
    if (mergeUUIDs.size === 1) {
      setNextMergeUUID(nextMergeUUID);
    } else {
      setNextMergeUUID(undefined);
    }
  }, [data?.getSmartOrderDetails, setNextMergeUUID]);

  // Report an error that indicates select permissions in query do not match
  // employee_smart_orders view select permissions
  // This is handled with an error state below
  useEffect(() => {
    if (statusReportedRef.current) {
      return;
    }
    statusReportedRef.current = true;
    if (
      data?.getSmartOrderDetails &&
      data.getSmartOrderDetails.length > 0 &&
      !data.getSmartOrderDetails.some((order) => order.smart_order)
    ) {
      captureException(new Error('smart order detail permission mismatch'), {
        extra: {
          mergeUUID,
          smartOrderUUID: orderUUID,
        },
      });
    }
  }, [data?.getSmartOrderDetails, mergeUUID, orderUUID]);

  const errorText = useMemo(() => {
    if (error || hasPollingError) {
      return i18n.t('toast.standard_error');
    }

    if (!visibleSmartOrderDetails) {
      return null;
    }

    const smartOrderDetails = visibleSmartOrderDetails;

    if (!smartOrderDetails.some((order) => order.smart_order)) {
      return i18n.t('order_inbound_error.no_results');
    }

    const orders = getOrderStatusInfos(
      smartOrderDetails,
      isEmailThreadsEnabled,
    );

    if (!shouldBlockOrders(orders, wasAlreadyClosed)) {
      return null;
    }

    return getInboundStatusMessage(orders);
  }, [
    visibleSmartOrderDetails,
    error,
    hasPollingError,
    isEmailThreadsEnabled,
    wasAlreadyClosed,
  ]);

  const {
    alternativeVariantPacksMap,
    isCorrectionMode,
    orderMetadata,
    previousVariantPacksMap,
    searchInputSubstitutionsMap,
    searchTermToSearchInputMap,
    smartOrderUUIDs,
    variantPackOrderCountsMap,
    workflowMode,
  } = useMemo<{
    alternativeVariantPacksMap: SwapSuggestionMap;
    isCorrectionMode: boolean;
    orderMetadata: OrderMetadata[];
    previousVariantPacksMap: SwapSuggestionMap;
    searchInputSubstitutionsMap: SwapSuggestionMap;
    searchTermToSearchInputMap: Map<string, string>;
    smartOrderUUIDs: string[];
    variantPackOrderCountsMap: VariantPackOrderCountsMap;
    workflowMode: OrderAgentWorkflowMode;
  }>(() => {
    const smartOrderDetails = visibleSmartOrderDetails;

    if (!smartOrderDetails || smartOrderDetails.length === 0) {
      return {
        alternativeVariantPacksMap: new Map(),
        isCorrectionMode: false,
        orderMetadata: [],
        previousVariantPacksMap: new Map(),
        searchInputSubstitutionsMap: new Map(),
        searchTermToSearchInputMap: new Map(),
        smartOrderUUIDs: [orderUUID],
        variantPackOrderCountsMap: new Map(),
        workflowMode: OrderAgentWorkflowMode.Submission,
      };
    }

    const {
      alternateMap,
      previousMatchMap,
      smartOrders,
      orderCountsMap,
      variantPackIdToSearchInputMap,
    } = smartOrderDetails.reduce<{
      alternateMap: SwapSuggestionMap;
      orderCountsMap: VariantPackOrderCountsMap;
      previousMatchMap: SwapSuggestionMap;
      smartOrders: SmartOrderFieldsFragment[];
      variantPackIdToSearchInputMap: Map<string, string[]>;
    }>(
      (acc, item) => {
        if (item.smart_order) {
          acc.smartOrders.push(item.smart_order);
        }

        item.search_inputs?.forEach((input) => {
          if (input.variant_pack_id) {
            // Map variant_pack_id to search_input_id
            const existing =
              acc.variantPackIdToSearchInputMap.get(input.variant_pack_id) ||
              [];
            acc.variantPackIdToSearchInputMap.set(input.variant_pack_id, [
              ...existing,
              input.search_input_id,
            ]);

            if (input.variant_pack_order_counts) {
              acc.orderCountsMap.set(
                input.variant_pack_id,
                input.variant_pack_order_counts,
              );
            }
          }
          const alternativeVPs = getSuggestions(
            input.alternative_variant_pack_matches,
            acc.orderCountsMap,
          );
          const previousVPs = getSuggestions(
            input.previous_variant_pack_matches,
            acc.orderCountsMap,
          );
          acc.alternateMap.set(input.search_input_id, alternativeVPs);
          acc.previousMatchMap.set(input.search_input_id, previousVPs);
        });

        return acc;
      },
      {
        alternateMap: new Map(),
        orderCountsMap: new Map(),
        previousMatchMap: new Map(),
        smartOrders: [],
        variantPackIdToSearchInputMap: new Map(),
      },
    );

    const substitutionsMap: SwapSuggestionMap = new Map();
    if (vpSubData) {
      vpSubData.getVariantPackSubstitutions.forEach((vpSub) => {
        const substitutionVPs = getSuggestions(
          vpSub.substitutions,
          orderCountsMap,
        );
        if (substitutionVPs.length > 0) {
          const searchInputIds =
            variantPackIdToSearchInputMap.get(vpSub.variant_pack_id) || [];

          searchInputIds.forEach((searchInputId) => {
            substitutionsMap.set(searchInputId, substitutionVPs);
          });
        }
      });
    }

    const allSmartOrders = [...smartOrders];
    const deduplicatedSmartOrders = allSmartOrders.filter(
      (order, index, self) =>
        self.findIndex((o) => o.uuid === order.uuid) === index,
    );

    // The flat search_inputs field (used above for alternate/previous match
    // suggestions) is only populated while an order still needs matching --
    // once it reaches correction mode it comes back empty, so old_search_term
    // -> search_input has to be resolved from each order's real relational
    // search_inputs instead. Keyed by order + search term because merged orders
    // and email threads can each carry their own input for the same term.
    const searchTermToSearchInputMap = new Map<string, string>();
    deduplicatedSmartOrders.forEach((order) => {
      order.search_inputs.forEach((input) => {
        searchTermToSearchInputMap.set(
          getCorrectionKey(order.uuid, input.search_term.uuid),
          input.uuid,
        );
      });
    });

    // Only reveal items for submitted/rejected/memorized orders when every
    // order in view is already processed, and then only the inputs that were
    // actually submitted (matched, non-zero) -- unmatched inputs stay on the
    // smart order after submission but were never part of the real order. If
    // it's a mixed set (e.g. an email thread where only some replies have been
    // submitted), keep hiding items that belong to the already-processed orders.
    const workflowMode = getWorkflowModeForStatuses(
      deduplicatedSmartOrders.map((order) => order.status as SmartOrderStatus),
    );
    const metadata = getOrderMetadata(deduplicatedSmartOrders, {
      searchInputsMode:
        workflowMode === OrderAgentWorkflowMode.Correction
          ? 'submitted'
          : 'effective',
    });

    return {
      alternativeVariantPacksMap: alternateMap,
      isCorrectionMode: workflowMode === OrderAgentWorkflowMode.Correction,
      orderMetadata: metadata,
      previousVariantPacksMap: previousMatchMap,
      searchInputSubstitutionsMap: substitutionsMap,
      searchTermToSearchInputMap,
      smartOrderUUIDs: deduplicatedSmartOrders.length
        ? deduplicatedSmartOrders.map((order) => order.uuid)
        : [orderUUID],
      variantPackOrderCountsMap: orderCountsMap,
      workflowMode,
    };
  }, [visibleSmartOrderDetails, vpSubData, orderUUID]);

  const {
    data: searchTermCorrectionsData,
    refetch: refetchSearchTermCorrections,
  } = useSmartOrderSearchTermCorrections(
    {
      locale: detailQueryVariables.locale,
      restaurantUUID: detailQueryVariables.restaurantUUID,
      skipGroupMemberships: detailQueryVariables.skipGroupMemberships,
      skipLiveSalt: detailQueryVariables.skipLiveSalt,
      skipSaltApi: detailQueryVariables.skipSaltApi,
      smartOrderUUIDs,
      useSaltCache: detailQueryVariables.useSaltCache,
    },
    !isCorrectionMode,
  );

  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();

  const correctionPreviewMap = useMemo<CorrectionPreviewMap>(() => {
    const map: CorrectionPreviewMap = new Map();
    searchTermCorrectionsData?.smart_order_search_term_corrections.forEach(
      (correction) => {
        const searchInputUUID = searchTermToSearchInputMap.get(
          getCorrectionKey(
            correction.smart_order_uuid,
            correction.old_search_term_uuid,
          ),
        );
        const variantsPack = correction.new_search_term.variants_pack;
        if (!searchInputUUID || !variantsPack) {
          return;
        }
        const metadata =
          adaptVariantPackToItemListOption(variantsPack).metadata;
        if (!metadata) {
          return;
        }
        map.set(searchInputUUID, metadata);
      },
    );
    return map;
  }, [
    searchTermCorrectionsData,
    searchTermToSearchInputMap,
    adaptVariantPackToItemListOption,
  ]);

  // Need to do this to avoid the following error:
  // https://github.com/facebook/react/issues/18178#issuecomment-595846312
  useEffect(() => {
    if (loading) {
      return;
    }
    // We only need to look at the first order to get the assigned employee. Here are the assumptions for multiple orders:
    // - If two or more orders are merged and one of them has an assigned employee, then that employee is assigned to all
    //   of the orders.
    // - If two or more orders are merged and no assigned employee is found, then there is no assigned employee
    // - If two or more orders are merged but there are different assigned employees, then all of the orders are set to
    //   unassigned.
    //
    // These should be guaranteed when the data is already passed in to this component.
    // TODO: Maybe we add some reconciliation process here if the assumptions listed above are wrong?
    const assignedEmployee = orderMetadata[0]?.assignedEmployee ?? null;

    onAssignedEmployeeChanged?.(assignedEmployee);

    if (orderMetadata[0]) {
      onOrderIdentityChanged?.({
        restaurantName: orderMetadata[0].restaurantName,
        supplierUUID: orderMetadata[0].supplierUUID,
      });
    }
  }, [
    loading,
    onAssignedEmployeeChanged,
    onOrderIdentityChanged,
    orderMetadata,
  ]);

  const splitContainerStyle = isSmallLayout ? styles.center : null;

  // unknownContactInfo isn't relevant if status changes after assigning the order
  // Check status before rendering unknown contact form
  const finalUnknownContactInfo = useMemo(() => {
    if (!unknownContactInfo) {
      return undefined;
    }

    if (
      (visibleSmartOrderDetails || []).some(
        (order) =>
          order.smart_order &&
          order.smart_order.status === SmartOrderStatus.UNKNOWN_CONTACT,
      )
    ) {
      return unknownContactInfo;
    }
    return undefined;
  }, [visibleSmartOrderDetails, unknownContactInfo]);

  const [detailContentWidth, setDetailContentWidth] = useState(0);

  const onDetailContentWidthChange = useCallback(
    ({ newDetailContentWidth }: { newDetailContentWidth: number }) => {
      setDetailContentWidth(newDetailContentWidth);
    },
    [],
  );

  // Only show loading skeleton for initial load or new order loading.
  // During refetch (when we already have data), keep form mounted and update seamlessly.
  if (isInitialLoad || isNewOrderLoading) {
    const skeletonContent = isSmallTabletLayout ? (
      <View style={styles.skeletonWrapper}>
        <OriginalInboundSkeleton />
      </View>
    ) : (
      <OriginalInboundSkeleton />
    );
    return (
      <InboundDetailSplitContainer
        containerStyle={splitContainerStyle}
        detailContent={
          <ActivityIndicator
            size="large"
            style={isSmallLayout ? undefined : styles.loadingSpacing}
          />
        }
        originalInboundContent={skeletonContent}
        originalInboundHeader={
          !isInboundsListExpanded && !isSmallLayout && !isSmallTabletLayout ? (
            <InboundListExpandButton
              onPress={() => setIsInboundsListExpanded(true)}
            />
          ) : isSmallLayout || isSmallTabletLayout ? (
            <HeaderBackButton onPress={() => onCloseProp?.()} />
          ) : null
        }
      />
    );
  }

  if (errorText) {
    return (
      <InboundDetailSplitContainer
        containerStyle={splitContainerStyle}
        detailContent={
          <EmptyStateMinimal
            containerStyle={
              isSmallLayout
                ? styles.emptyStateContainerSmall
                : styles.emptyStateContainerLarge
            }
            ImageComponent={WheyIcon}
            text={errorText}
          />
        }
        originalInboundHeader={
          !isInboundsListExpanded && !isSmallLayout && !isSmallTabletLayout ? (
            <InboundListExpandButton
              onPress={() => setIsInboundsListExpanded(true)}
            />
          ) : isSmallLayout || isSmallTabletLayout ? (
            <HeaderBackButton onPress={() => onCloseProp?.()} />
          ) : null
        }
      />
    );
  }

  // guard against empty orderMetadata, which may happen after a user submits or rejects an order
  if (orderMetadata.length === 0) {
    return null;
  }

  return (
    <OrderInboundsDetail
      {...restDetailProps}
      activeRestaurant={activeRestaurant}
      alternativeVariantPacksMap={alternativeVariantPacksMap}
      assignSmartOrder={assignUnknownContact}
      correctionPreviewMap={correctionPreviewMap}
      isInboundsListExpanded={isInboundsListExpanded}
      isProcessing={isOrderProcessing}
      isProcessingReply={isProcessingReply}
      onRegenerateSmartOrder={onRegenerateSmartOrder}
      onResize={onDetailContentWidthChange}
      orderMetadata={orderMetadata}
      orderUUID={orderUUID}
      previousVariantPacksMap={previousVariantPacksMap}
      refetchOrderDetails={refetch}
      refetchSearchTermCorrections={refetchSearchTermCorrections}
      regenerateSmartOrders={reassignSmartOrders}
      rehydrateKey={replyHydrationKey}
      savedVariantPackOrderCountsMap={variantPackOrderCountsMap}
      searchInputSubstitutionsMap={searchInputSubstitutionsMap}
      selectNextSmartOrder={selectNextSmartOrder}
      setIsInboundsListExpanded={setIsInboundsListExpanded}
      setOrderInboundsListDrawerOpen={setOrderInboundsListDrawerOpen}
      showItemListView={
        isLargeLayout && detailContentWidth > DETAIL_CONTENT_LEGIBLE_MIN_WIDTH
      }
      unknownContactInfo={finalUnknownContactInfo}
      workflowMode={workflowMode}
    >
      {children}
    </OrderInboundsDetail>
  );
};

const useStyles = createStyles((theme) => ({
  center: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateContainerLarge: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  emptyStateContainerSmall: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  loadingSpacing: {
    marginTop: SCREEN_PADDING,
  },
  skeletonWrapper: {
    flex: 1,
  },
}));
